import React, {useMemo, useState} from 'react';
import {Button, Col, Form, ListGroup, Row, InputGroup as BsInputGroup} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronLeft, faChevronRight, faTimes} from '@fortawesome/free-solid-svg-icons';
import {WebsitesData} from "../Models/Models";
import {EntityColors} from "../Themes/EntityColors";

type WebsitePickerProps = {
    items: WebsitesData[];
    value: number[];
    onChange: (ids: number[]) => void;
    accentColor?: string;
    maxHeight?: number;
    leftPlaceholder?: string;
    rightPlaceholder?: string;
    addAllLabel?: string;
    removeAllLabel?: string;
    addLabel?: string;
    removeLabel?: string;
};

const WebsitePicker: React.FC<WebsitePickerProps> = ({
                                                         items,
                                                         value,
                                                         onChange,
                                                         accentColor = EntityColors.user.action,
                                                         maxHeight = 280,
                                                         leftPlaceholder = 'Hľadať v dostupných',
                                                         rightPlaceholder = 'Hľadať v pridelených',
                                                         addAllLabel = 'Pridať všetky',
                                                         removeAllLabel = 'Odobrať všetky',
                                                         addLabel = 'pridať',
                                                         removeLabel = 'odobrať',
                                                     }) => {
    const [searchAvailable, setSearchAvailable] = useState('');
    const [searchAssigned, setSearchAssigned] = useState('');
    const [selectedAvailable, setSelectedAvailable] = useState<Set<number>>(new Set());
    const [selectedAssigned, setSelectedAssigned] = useState<Set<number>>(new Set());

    const available = useMemo(
        () => items.filter(x => !value.includes(x.id)).sort((a, b) => a.shortName.localeCompare(b.shortName)),
        [items, value]
    );
    const assigned = useMemo(
        () => items.filter(x => value.includes(x.id)).sort((a, b) => a.shortName.localeCompare(b.shortName)),
        [items, value]
    );

    const filteredAvailable = useMemo(
        () => available.filter(s => s.shortName.toLowerCase().includes(searchAvailable.toLowerCase())),
        [available, searchAvailable]
    );
    const filteredAssigned = useMemo(
        () => assigned.filter(s => s.shortName.toLowerCase().includes(searchAssigned.toLowerCase())),
        [assigned, searchAssigned]
    );

    const toggleAvailable = (id: number) => {
        const next = new Set(selectedAvailable);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelectedAvailable(next);
    };

    const toggleAssigned = (id: number) => {
        const next = new Set(selectedAssigned);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelectedAssigned(next);
    };

    const addSelected = () => {
        if (selectedAvailable.size === 0) return;
        const next = Array.from(new Set([...value, ...Array.from(selectedAvailable)]));
        onChange(next);
        setSelectedAvailable(new Set());
    };

    const removeSelected = () => {
        if (selectedAssigned.size === 0) return;
        const block = new Set(selectedAssigned);
        const next = value.filter(id => !block.has(id));
        onChange(next);
        setSelectedAssigned(new Set());
    };

    const addAllFiltered = () => {
        if (filteredAvailable.length === 0) return;
        const ids = filteredAvailable.map(s => s.id);
        const next = Array.from(new Set([...value, ...ids]));
        onChange(next);
        setSelectedAvailable(new Set());
    };

    const removeAllFiltered = () => {
        if (filteredAssigned.length === 0) return;
        const ids = new Set(filteredAssigned.map(s => s.id));
        const next = value.filter(id => !ids.has(id));
        onChange(next);
        setSelectedAssigned(new Set());
    };

    return (
        <Row className="g-3 align-items-stretch">
            <Col md={5}>
                <BsInputGroup className="mb-2">
                    <Form.Control
                        placeholder={leftPlaceholder}
                        value={searchAvailable}
                        onChange={e => setSearchAvailable(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                    />
                    {searchAvailable && (
                        <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={() => setSearchAvailable('')}
                        >
                            <FontAwesomeIcon icon={faTimes}/>
                        </Button>
                    )}
                    <Button
                        disabled={available.length === 0 || filteredAvailable.length === 0}
                        type="button"
                        variant="outline-primary"
                        onMouseDown={e => e.preventDefault()}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); addAllFiltered(); }}
                    >
                        {addAllLabel}
                    </Button>
                </BsInputGroup>

                <div style={{border: '1px solid var(--st-line)', borderRadius: 10, maxHeight, overflowY: 'auto'}}>
                    <ListGroup variant="flush">
                        {filteredAvailable.map(s => (
                            <ListGroup.Item
                                key={s.id}
                                action
                                active={selectedAvailable.has(s.id)}
                                onClick={e => { e.preventDefault(); toggleAvailable(s.id); }}
                            >
                                {s.shortName}
                            </ListGroup.Item>
                        ))}
                        {filteredAvailable.length === 0 && <ListGroup.Item disabled>Žiadne položky</ListGroup.Item>}
                    </ListGroup>
                </div>
                <div className="mt-2 mb-2 text-muted small">Dostupné: {available.length}</div>
            </Col>

            <Col md={2} className="d-flex flex-column justify-content-center align-items-center gap-2">
                <Button
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={e => { e.preventDefault(); e.stopPropagation(); addSelected(); }}
                    disabled={selectedAvailable.size === 0}
                    style={{ backgroundColor: accentColor, borderColor: accentColor }}
                >
                    <FontAwesomeIcon icon={faChevronRight}/> {addLabel}
                </Button>
                <Button
                    type="button"
                    variant="outline-secondary"
                    onMouseDown={e => e.preventDefault()}
                    onClick={e => { e.preventDefault(); e.stopPropagation(); removeSelected(); }}
                    disabled={selectedAssigned.size === 0}
                >
                    <FontAwesomeIcon icon={faChevronLeft}/> {removeLabel}
                </Button>
            </Col>

            <Col md={5}>
                <BsInputGroup className="mb-2">
                    <Form.Control
                        placeholder={rightPlaceholder}
                        value={searchAssigned}
                        onChange={e => setSearchAssigned(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                    />
                    {searchAssigned && (
                        <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={() => setSearchAssigned('')}
                        >
                            <FontAwesomeIcon icon={faTimes}/>
                        </Button>
                    )}
                    <Button
                        disabled={assigned.length === 0 || filteredAssigned.length === 0}
                        type="button"
                        variant="outline-danger"
                        onMouseDown={e => e.preventDefault()}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); removeAllFiltered(); }}
                    >
                        {removeAllLabel}
                    </Button>
                </BsInputGroup>

                <div style={{border: '1px solid var(--st-line)', borderRadius: 10, maxHeight, overflowY: 'auto'}}>
                    <ListGroup variant="flush">
                        {filteredAssigned.map(s => (
                            <ListGroup.Item
                                key={s.id}
                                action
                                active={selectedAssigned.has(s.id)}
                                onClick={e => { e.preventDefault(); toggleAssigned(s.id); }}
                            >
                                {s.shortName}
                            </ListGroup.Item>
                        ))}
                        {filteredAssigned.length === 0 && <ListGroup.Item disabled>Žiadne položky</ListGroup.Item>}
                    </ListGroup>
                </div>
                <div className="mt-2 mb-2 text-muted small">Pridelené: {assigned.length}</div>
            </Col>
        </Row>
    );
};

export default WebsitePicker;
